import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState, AppDispatch } from '../../redux/store';
import { logout } from '../../redux/auth/authSlice';
import Sidebar from '../../components/sidebar/Sidebar';
import { Button } from '../../components/ui/button';
import { Menu, RefreshCw, BarChart3, TrendingUp, Image, MessageSquare, Zap, DollarSign, Coins } from 'lucide-react';
import { ResponsiveAppShell, TaskyHeaderActions } from '../../components/tasky';
import usageService, {
  UsageSummary,
  DailyUsage,
  FeatureUsage,
  PeriodType,
} from '../../services/api/usageService';

// ============================================================
// Tailwind class constants (旧 styled-components)
// ============================================================

const PAGE_WRAPPER = 'flex min-h-[calc(100dvh_-_56px)] bg-warm-bg lg:h-screen lg:min-h-0';

const PAGE_TITLE_WRAPPER = 'flex items-center gap-2.5';

const MAIN_CONTENT = 'flex-1 flex flex-col overflow-hidden';

const HEADER = 'min-h-[56px] border-b border-b-warm-border flex flex-wrap items-center justify-between gap-y-2 px-4 py-2 bg-white max-[1023px]:hidden';

const HEADER_LEFT = 'flex items-center gap-3';

const HEADER_RIGHT = 'flex flex-wrap items-center justify-end gap-3 max-[640px]:items-stretch max-[640px]:flex-col max-[640px]:w-full';

const PAGE_TITLE = 'text-[18px] font-semibold text-dark m-0';

const PERIOD_SELECTOR = 'flex bg-warm-surface border border-warm-border rounded-lg p-0.5 max-[640px]:overflow-x-auto';

const PERIOD_BUTTON_BASE = 'px-3 py-1.5 border-0 rounded-md text-[13px] font-medium cursor-pointer transition-all duration-200 ease-[ease] hover:text-dark';
const PERIOD_BUTTON_ACTIVE = 'bg-white text-dark shadow-[0_1px_2px_rgba(0,0,0,0.05)]';
const PERIOD_BUTTON_IDLE = 'bg-transparent text-gray-500 shadow-none';

const CONTENT_WRAPPER = 'flex-1 overflow-y-auto overflow-x-hidden p-6 max-[640px]:p-4';

const ERROR_BANNER = 'bg-red-100 border border-red-200 text-destructive px-4 py-3 rounded-lg mb-6';

const LOADING_CONTAINER = 'flex flex-col items-center justify-center h-[300px] gap-4';

const LOADING_TEXT = 'text-gray-500 text-[14px]';

const SUMMARY_GRID = 'grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 mb-8 max-[640px]:grid-cols-1';

const SUMMARY_CARD = 'bg-white border border-warm-border rounded-xl p-5 flex items-center gap-4 shadow-[0_1px_3px_rgba(29,25,39,0.04)]';

// 旧 SummaryIcon の `background: ${color}15` は 8桁hex（alpha 0x15）で再現
const SUMMARY_ICON_BASE = 'w-12 h-12 rounded-xl flex items-center justify-center';
const SUMMARY_ICON_PRIMARY = 'bg-[#616FC915] text-[#616FC9]';
const SUMMARY_ICON_EMERALD = 'bg-[#10B98115] text-[#10B981]';
const SUMMARY_ICON_VIOLET = 'bg-[#8B5CF615] text-[#8B5CF6]';
const SUMMARY_ICON_AMBER = 'bg-[#F59E0B15] text-[#F59E0B]';

const SUMMARY_CONTENT = 'flex-1';

const SUMMARY_VALUE = 'text-[24px] font-semibold text-dark';

const SUMMARY_LABEL = 'text-[13px] text-gray-500 mt-0.5';

const SECTION = 'bg-white border border-warm-border rounded-xl p-5 mb-6 shadow-[0_1px_3px_rgba(29,25,39,0.04)] max-[640px]:p-4';

const SECTION_TITLE = 'text-[16px] font-semibold text-dark m-0 mb-4';

const FEATURE_GRID = 'grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4 max-[640px]:grid-cols-1';

const FEATURE_CARD = 'bg-warm-surface border border-warm-border rounded-lg p-4';

const FEATURE_HEADER = 'flex items-center gap-2.5 mb-3';

const FEATURE_ICON_WRAPPER = 'w-8 h-8 rounded-md flex items-center justify-center';

const FEATURE_NAME = 'text-[14px] font-medium text-dark';

const FEATURE_STATS = 'flex gap-4 flex-wrap';

const FEATURE_STAT_VALUE = 'text-[16px] font-semibold text-dark';

const FEATURE_STAT_LABEL = 'text-[11px] text-gray-500';

const EMPTY_STATE = 'text-center p-10 text-gray-500';

const DAILY_CHART_CONTAINER = 'h-[200px] py-4 overflow-x-auto';

const DAILY_CHART = 'flex items-end justify-between h-full gap-2 min-w-[420px] sm:min-w-0';

const DAILY_BAR = 'flex-1 flex flex-col items-center h-full relative [&:hover>div:last-child]:opacity-100';

const BAR_FILL = 'w-full max-w-[40px] bg-[linear-gradient(180deg,#616FC9_0%,#4F5BB5_100%)] rounded-t min-h-[4px] mt-auto transition-[height] duration-300 ease-[ease]';

const BAR_LABEL = 'text-[11px] text-gray-500 mt-1.5';

const BAR_TOOLTIP = 'absolute bottom-full left-1/2 -translate-x-1/2 bg-dark text-white px-2 py-1 rounded text-[12px] whitespace-nowrap opacity-0 transition-opacity duration-200 ease-[ease] pointer-events-none mb-1';

const TOKEN_BREAKDOWN = 'flex items-center justify-center gap-10 p-4 bg-warm-surface rounded-lg max-[640px]:items-stretch max-[640px]:flex-col max-[640px]:gap-4';

const TOKEN_ITEM = 'text-center';

const TOKEN_LABEL = 'text-[13px] text-gray-500 mb-1';

const TOKEN_VALUE = 'text-[20px] font-semibold text-dark';

const TOKEN_DIVIDER = 'w-px h-10 bg-warm-border';

const UsageDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // State
  const [period, setPeriod] = useState<PeriodType>('month');
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [dailyUsage, setDailyUsage] = useState<DailyUsage[]>([]);
  const [featureUsage, setFeatureUsage] = useState<FeatureUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [summaryRes, dailyRes, featureRes] = await Promise.all([
        usageService.getSummary(period),
        usageService.getDaily(period === 'day' ? 7 : period === 'week' ? 14 : period === 'month' ? 30 : 90),
        usageService.getByFeature(period),
      ]);

      if (summaryRes.success) {
        setSummary(summaryRes.data);
      }
      if (dailyRes.success) {
        setDailyUsage(dailyRes.data);
      }
      if (featureRes.success) {
        setFeatureUsage(featureRes.data);
      }
    } catch (err: any) {
      setError(err.message || 'データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatCost = (cost: number): string => {
    return `$${cost.toFixed(4)}`;
  };

  const formatCredits = (credits: number): string => {
    if (credits >= 1000) {
      return (credits / 1000).toFixed(1) + 'K';
    }
    return credits.toFixed(1);
  };

  const getFeatureIcon = (featureType: string) => {
    switch (featureType) {
      case 'chat':
        return <MessageSquare size={16} />;
      case 'super_agent':
        return <Zap size={16} />;
      case 'image_generation':
      case 'manga_image':
        return <Image size={16} />;
      default:
        return <BarChart3 size={16} />;
    }
  };

  // 旧 getFeatureColor。`background: ${color}15` / `color: ${color}` を
  // 完全なクラス文字列（8桁hex = alpha 0x15）に置き換え
  const getFeatureTone = (featureType: string): string => {
    switch (featureType) {
      case 'chat':
        return 'bg-[#616FC915] text-[#616FC9]';
      case 'super_agent':
        return 'bg-[#9675C915] text-[#9675C9]';
      case 'image_generation':
        return 'bg-[#44C2A415] text-[#44C2A4]';
      case 'manga_story':
        return 'bg-[#C9A06115] text-[#C9A061]';
      case 'manga_image':
        return 'bg-[#C9616215] text-[#C96162]';
      case 'product_extraction':
        return 'bg-[#616FC915] text-[#616FC9]';
      default:
        return 'bg-[#6B728015] text-[#6B7280]';
    }
  };

  const sidebar = (
    <Sidebar
      onLogout={handleLogout}
      onToggleCollapse={setSidebarCollapsed}
      collapsed={sidebarCollapsed}
    />
  );
  const mobileSidebar = <Sidebar onLogout={handleLogout} />;

  return (
    <ResponsiveAppShell sidebar={sidebar} mobileSidebar={mobileSidebar} topbarTitle="利用状況">
    <div className={PAGE_WRAPPER}>
      <div className={MAIN_CONTENT}>
        <div className={HEADER}>
          <div className={HEADER_LEFT}>
            {sidebarCollapsed && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarCollapsed(false)}
                className="p-1 h-8 w-8 hover:bg-gray-100 rounded-md"
                title="サイドバーを展開"
              >
                <Menu className="w-4 h-4" />
              </Button>
            )}
            <div className={PAGE_TITLE_WRAPPER}>
              <BarChart3 className="w-[22px] h-[22px] text-primary" />
              <h1 className={PAGE_TITLE}>利用状況</h1>
            </div>
          </div>
          <div className={HEADER_RIGHT}>
            <div className={PERIOD_SELECTOR} data-tutorial="usage-period">
              {(['day', 'week', 'month', 'year'] as PeriodType[]).map((p) => (
                <button
                  key={p}
                  className={`${PERIOD_BUTTON_BASE} ${period === p ? PERIOD_BUTTON_ACTIVE : PERIOD_BUTTON_IDLE}`}
                  onClick={() => setPeriod(p)}
                >
                  {p === 'day' ? '24時間' : p === 'week' ? '1週間' : p === 'month' ? '1ヶ月' : '1年'}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadData}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              更新
            </Button>
            <TaskyHeaderActions />
          </div>
        </div>

        <div className={CONTENT_WRAPPER}>
          {error && (
            <div className={ERROR_BANNER}>
              {error}
            </div>
          )}

          {loading && !summary ? (
            <div className={LOADING_CONTAINER}>
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
              <p className={LOADING_TEXT}>データを読み込み中...</p>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className={SUMMARY_GRID} data-tutorial="usage-summary">
                <div className={SUMMARY_CARD}>
                  <div className={`${SUMMARY_ICON_BASE} ${SUMMARY_ICON_PRIMARY}`}>
                    <BarChart3 size={20} />
                  </div>
                  <div className={SUMMARY_CONTENT}>
                    <div className={SUMMARY_VALUE}>{formatNumber(summary?.totalTokens || 0)}</div>
                    <div className={SUMMARY_LABEL}>総クレジット</div>
                  </div>
                </div>

                <div className={SUMMARY_CARD}>
                  <div className={`${SUMMARY_ICON_BASE} ${SUMMARY_ICON_EMERALD}`}>
                    <Image size={20} />
                  </div>
                  <div className={SUMMARY_CONTENT}>
                    <div className={SUMMARY_VALUE}>{summary?.totalImageCount || 0}</div>
                    <div className={SUMMARY_LABEL}>画像生成数</div>
                  </div>
                </div>

                <div className={SUMMARY_CARD}>
                  <div className={`${SUMMARY_ICON_BASE} ${SUMMARY_ICON_VIOLET}`}>
                    <TrendingUp size={20} />
                  </div>
                  <div className={SUMMARY_CONTENT}>
                    <div className={SUMMARY_VALUE}>{summary?.totalRequests || 0}</div>
                    <div className={SUMMARY_LABEL}>リクエスト数</div>
                  </div>
                </div>

                <div className={SUMMARY_CARD}>
                  <div className={`${SUMMARY_ICON_BASE} ${SUMMARY_ICON_AMBER}`}>
                    <Coins size={20} />
                  </div>
                  <div className={SUMMARY_CONTENT}>
                    <div className={SUMMARY_VALUE}>{formatCredits(summary?.totalCredits || 0)}</div>
                    <div className={SUMMARY_LABEL}>Taskyクレジット</div>
                  </div>
                </div>
              </div>

              {/* Feature Usage */}
              <div className={SECTION} data-tutorial="usage-features">
                <h2 className={SECTION_TITLE}>機能別使用量</h2>
                <div className={FEATURE_GRID}>
                  {featureUsage.length > 0 ? (
                    featureUsage.map((feature) => (
                      <div className={FEATURE_CARD} key={feature.feature_type}>
                        <div className={FEATURE_HEADER}>
                          <div className={`${FEATURE_ICON_WRAPPER} ${getFeatureTone(feature.feature_type)}`}>
                            {getFeatureIcon(feature.feature_type)}
                          </div>
                          <div className={FEATURE_NAME}>{feature.feature_label}</div>
                        </div>
                        <div className={FEATURE_STATS}>
                          <div>
                            <div className={FEATURE_STAT_VALUE}>{formatNumber(feature.total_tokens)}</div>
                            <div className={FEATURE_STAT_LABEL}>トークン</div>
                          </div>
                          <div>
                            <div className={FEATURE_STAT_VALUE}>{feature.total_requests}</div>
                            <div className={FEATURE_STAT_LABEL}>リクエスト</div>
                          </div>
                          {feature.total_image_count > 0 && (
                            <div>
                              <div className={FEATURE_STAT_VALUE}>{feature.total_image_count}</div>
                              <div className={FEATURE_STAT_LABEL}>画像</div>
                            </div>
                          )}
                          <div>
                            <div className={FEATURE_STAT_VALUE}>{formatCredits(feature.total_credits || 0)}</div>
                            <div className={FEATURE_STAT_LABEL}>クレジット</div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={EMPTY_STATE}>
                      この期間の使用データがありません
                    </div>
                  )}
                </div>
              </div>

              {/* Daily Usage Chart */}
              <div className={SECTION} data-tutorial="usage-daily-chart">
                <h2 className={SECTION_TITLE}>日別使用量</h2>
                {dailyUsage.length > 0 ? (
                  <div className={DAILY_CHART_CONTAINER}>
                    <div className={DAILY_CHART}>
                      {dailyUsage.slice(0, 14).reverse().map((day) => {
                        const maxTokens = Math.max(...dailyUsage.map(d => d.total_tokens), 1);
                        const height = (day.total_tokens / maxTokens) * 100;
                        return (
                          <div className={DAILY_BAR} key={day.date}>
                            <div className={BAR_FILL} style={{ height: `${height}%` }} />
                            <div className={BAR_LABEL}>{new Date(day.date).getDate()}日</div>
                            <div className={BAR_TOOLTIP}>
                              {formatNumber(day.total_tokens)} トークン
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className={EMPTY_STATE}>
                    この期間の日別データがありません
                  </div>
                )}
              </div>

              {/* Token Breakdown */}
              {summary && (
                <div className={SECTION}>
                  <h2 className={SECTION_TITLE}>トークン内訳</h2>
                  <div className={TOKEN_BREAKDOWN}>
                    <div className={TOKEN_ITEM}>
                      <div className={TOKEN_LABEL}>入力トークン</div>
                      <div className={TOKEN_VALUE}>{formatNumber(summary.totalInputTokens)}</div>
                    </div>
                    <div className={TOKEN_DIVIDER} />
                    <div className={TOKEN_ITEM}>
                      <div className={TOKEN_LABEL}>出力トークン</div>
                      <div className={TOKEN_VALUE}>{formatNumber(summary.totalOutputTokens)}</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    </ResponsiveAppShell>
  );
};

export default UsageDashboard;
